import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealText } from "./motion";

const SLIDES = [
  { kicker: "Chapter 01 — Listen", headline: ["Community", "research."], text: "Every intervention begins with listening, research consent and community validation.", image: "https://images.unsplash.com/photo-1565958923272-e96dbb1e8414?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80", cta1: { text: "How we work", href: "/operating-model" }, cta2: { text: "Report a need", href: "/report-a-need" } },
  { kicker: "Chapter 02 — Respond", headline: ["Food, emergency", "& housing."], text: "Rapid, dignified support for families and communities in crisis.", image: "https://images.unsplash.com/photo-1586900632557-b99cf3b226cd?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80", cta1: { text: "Get help", href: "/get-help" }, cta2: { text: "Emergency fund", href: "/emergency" } },
  { kicker: "Chapter 03 — Empower", headline: ["Empower women", "and youth."], text: "Livelihoods, agriculture value chains and micro-enterprise support.", image: "https://images.unsplash.com/photo-1509099381441-ea3c0cf98b94?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80", cta1: { text: "Register free", href: "/empowerment/register" }, cta2: { text: "Empowerment programme", href: "/empowerment" } },
  { kicker: "Chapter 04 — Build", headline: ["Water, energy &", "infrastructure."], text: "Solar mini-grids, boreholes and community assets owned and maintained locally.", image: "https://images.unsplash.com/photo-1776090416729-20bedcc1a207?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80", cta1: { text: "Sponsor a project", href: "/sponsor-a-project" }, cta2: { text: "Project map", href: "/impact-map" } },
  { kicker: "Chapter 05 — Educate", headline: ["Learning", "& skills."], text: "Free courses, financial education, vocational skills and scholarships.", image: "https://images.unsplash.com/photo-1617056239820-8ce90ba48193?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80", cta1: { text: "Start learning", href: "/learn" }, cta2: { text: "Education support", href: "/programmes/education" } },
  { kicker: "Chapter 06 — Sustain", headline: ["Community ownership", "& long-term impact."], text: "Maintenance, monitoring and public reporting long after handover.", image: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80", cta1: { text: "Impact & transparency", href: "/transparency" }, cta2: { text: "Verify a reference", href: "/verify" } },
];

export default function HeroSlider() {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start" });
  const [i, setI] = useState(0);
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 800], [0, 160]);
  const overlayY = useTransform(scrollY, [0, 800], [0, 40]);

  useEffect(() => {
    if (!embla) return;
    const on = () => setI(embla.selectedScrollSnap());
    embla.on("select", on);
    const t = setInterval(() => embla.scrollNext(), 6800);
    return () => { clearInterval(t); embla.off("select", on); };
  }, [embla]);

  const scrollTo = useCallback((n) => embla && embla.scrollTo(n), [embla]);

  return (
    <section className="relative h-[85svh] min-h-[560px] bg-[#155e3f] overflow-hidden" data-testid="hero-slider">
      {/* Grain + vignette overlays (fixed, above image parallax) */}
      <motion.div
        style={{ y: overlayY }}
        className="absolute inset-0 pointer-events-none z-20"
      >
        <div className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
          style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.9'/></svg>\")" }}
        />
      </motion.div>

      {/* Slides */}
      <div ref={emblaRef} className="h-full">
        <div className="flex h-full">
          {SLIDES.map((s, idx) => (
            <div key={s.kicker} className="flex-[0_0_100%] relative h-full">
              <motion.div style={{ y: parallaxY }} className="absolute inset-0">
                <img src={s.image} alt={`${s.kicker}: ${s.headline.join(" ")}`} loading={i === 0 ? "eager" : "lazy"} decoding="async" className="absolute inset-0 w-full h-full object-cover scale-[1.08]" />
              </motion.div>
              <div className="absolute inset-0 bg-gradient-to-r from-[#155e3f] via-[#155e3f]/85 to-[#155e3f]/45" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#155e3f]/60 via-transparent to-[#155e3f]/75" />
            </div>
          ))}
        </div>
      </div>

      {/* Content overlay (static; content is per-active-slide) */}
      <div className="absolute inset-0 z-30 flex items-end pb-14 md:pb-24 pointer-events-none">
        <div className="dk-container w-full">
          <div className="max-w-4xl pointer-events-auto">
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[#d6aa43] tracking-[0.35em] text-[11px] font-semibold uppercase mb-5" data-testid="hero-kicker">
                {SLIDES[i].kicker}
              </div>
              <h1 className="font-serif text-white font-bold leading-[0.98] text-5xl sm:text-6xl md:text-7xl lg:text-[96px]">
                <RevealText delay={0.1}>
                  {SLIDES[i].headline.map((line, li) => (
                    <span key={line} className={li === SLIDES[i].headline.length - 1 ? "italic text-[#d6aa43]" : ""}>{line}</span>
                  ))}
                </RevealText>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.55 }}
                className="mt-6 md:mt-8 text-white text-lg md:text-xl max-w-2xl"
              >
                {SLIDES[i].text}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.75 }}
                className="mt-8 md:mt-10 flex flex-wrap gap-4"
              >
                <Link to={SLIDES[i].cta1.href} className="dk-btn-primary" data-testid={`hero-cta-primary-${i}`}>
                  {SLIDES[i].cta1.text} <ArrowRight size={16} />
                </Link>
                <Link to={SLIDES[i].cta2.href} className="dk-btn-outline" data-testid={`hero-cta-secondary-${i}`}>
                  {SLIDES[i].cta2.text}
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Slide indicator + controls */}
      <div className="absolute bottom-6 md:bottom-8 right-6 md:right-10 z-30 flex items-center gap-3">
        <div className="hidden sm:flex items-center gap-2">
          {SLIDES.map((s, idx) => (
            <button
              key={s.kicker}
              onClick={() => scrollTo(idx)}
              aria-label={`Slide ${idx + 1}`}
              data-testid={`hero-dot-${idx}`}
              className={`h-[3px] transition-all duration-500 ${i === idx ? "w-10 bg-[#d6aa43]" : "w-5 bg-white/40 hover:bg-white/60"}`}
            />
          ))}
        </div>
        <div className="flex gap-1.5">
          <button onClick={() => embla && embla.scrollPrev()} className="w-10 h-10 rounded-full bg-white/10 backdrop-blur border border-white/15 text-white flex items-center justify-center hover:bg-[#d6aa43] hover:text-[#0d1724] transition" data-testid="hero-prev">
            <ChevronLeft size={18} />
          </button>
          <button onClick={() => embla && embla.scrollNext()} className="w-10 h-10 rounded-full bg-white/10 backdrop-blur border border-white/15 text-white flex items-center justify-center hover:bg-[#d6aa43] hover:text-[#0d1724] transition" data-testid="hero-next">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Left slide counter */}
      <div className="absolute bottom-6 md:bottom-10 left-6 md:left-10 z-30 flex items-center gap-4">
        <div className="font-serif text-[#d6aa43] text-3xl md:text-4xl font-bold tabular-nums">{String(i + 1).padStart(2, "0")}</div>
        <div className="w-16 h-px bg-white/25" />
        <div className="text-[10px] tracking-[0.3em] text-white/90 font-semibold">{String(SLIDES.length).padStart(2, "0")}</div>
      </div>
    </section>
  );
}
